import { stytch } from '@/services/stytch';
import { isDev } from '@/util/env';
import { logError } from '@/util/logger';
import { ApolloClient, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ResponseGraphQLError } from '@common/errors';
import Router from 'next/router';
import cache from './cache';

const httpLink = createHttpLink({
    uri: `${process.env.NEXT_PUBLIC_PLENTY_API_URL}/graphql`,
});

const errorLink = onError(({ graphQLErrors }) => {
    graphQLErrors?.forEach((error) => {
        const { message, extensions } = error as ResponseGraphQLError;
        switch (message) {
            case 'CustomError': {
                if (extensions?.code === 'UNDER_REVIEW') {
                    stytch.session.revoke();
                    const alreadyRedirected =
                        globalThis.localStorage.getItem(
                            'REDIRECTED_UNDER_REVIEW_TO_SIGNIN'
                        ) === 'YES';
                    if (alreadyRedirected) {
                        // Show the /review page
                        Router.replace('/review');
                    } else {
                        // Redirect to /signin page the first time this error is encountered
                        globalThis.localStorage.setItem(
                            'REDIRECTED_UNDER_REVIEW_TO_SIGNIN',
                            'YES'
                        );
                        Router.replace('/signin');
                    }
                } else if (extensions?.code === 'SIGNUPS_DISABLED') {
                    Router.replace('/acquisition-announcement');
                }
                break;
            }
        }
    });
});

const authLink = setContext(async (_, { headers }) => {
    let jwt = stytch.session.getTokens()?.session_jwt;
    if (!jwt) {
        jwt = await waitForSessionJwt();
    }

    return {
        headers: {
            ...headers,
            authorization: `Bearer ${jwt}`,
        },
    };
});

const client = new ApolloClient({
    link: from([authLink, errorLink, httpLink]),
    connectToDevTools: isDev,
    cache,
});

const waitForSessionJwt = async (): Promise<string> => {
    return await Promise.race<string>([
        new Promise<string>((resolve) => {
            stytch.session.onChange(() => {
                const jwt = stytch.session.getTokens()?.session_jwt;
                if (jwt) {
                    resolve(jwt);
                }
            });
        }),
        // After 5 seconds, make a final attempt and throw an error if undefined
        new Promise<string>((resolve, reject) => {
            setTimeout(() => {
                const jwt = stytch.session.getTokens()?.session_jwt;
                if (jwt) {
                    resolve(jwt);
                } else {
                    logError('Timeout while waiting for session');
                    reject(new Error('Timeout while waiting for session'));
                }
            }, 5000);
        }),
    ]);
};

export default client;
